/**
 * Adds a dot-pagination row to Dawn's native product media gallery,
 * synced to the existing .slider-counter and thumbnail buttons.
 * Does not alter media-gallery.js / slider-component.js behaviour —
 * it only reads the current slide and reuses the existing thumbnail
 * click handlers to navigate.
 */
(function () {
  // Positioning the "New Arrival" badge relative to its original
  // wrapper (.product__media-wrapper) hasn't held up — that wrapper
  // doesn't reliably render where the visible image actually sits.
  // Instead of fighting that, physically move the badge to live
  // inside the real image box (.product-media-container of the first
  // slide) so it inherits the correct position automatically instead
  // of us guessing an offset from an unreliable ancestor.
  var badgeRelocated = false;

  function relocateBadge() {
    var badge = document.querySelector('.custom_badge_container');
    if (!badge) return;

    // One image on screen at a time now (mobile and desktop both, since
    // the desktop 2-up grid was reverted) — always target whichever
    // slide is actually active.
    var target = document.querySelector('.product__media-item.is-active .product-media-container') ||
      document.querySelector('.product__media-item .product-media-container') ||
      document.querySelector('.product-media-container') ||
      document.querySelector('.product__media-item .media') ||
      document.querySelector('.product__media-item');
    if (!target) return;

    if (badge.parentElement !== target) {
      target.style.setProperty('position', 'relative', 'important');
      target.appendChild(badge);
      badgeRelocated = true;
    }

    badge.style.setProperty('position', 'absolute', 'important');
    badge.style.setProperty('top', '26px', 'important');
    badge.style.setProperty('right', '24px', 'important');
    badge.style.setProperty('left', 'auto', 'important');
    badge.style.setProperty('bottom', 'auto', 'important');
    // Was hardcoded to 7 here, which (being an inline style) beat the
    // z-index:2 set in component-gallery-dots.css regardless of what
    // that stylesheet said — an inline style always wins over an
    // external stylesheet rule even when both carry !important, since
    // inline declarations take precedence at the specificity tie-break.
    // That's what let this badge keep painting over the site's sticky
    // header (z-index: 3) on mobile after a small scroll, even once the
    // CSS file itself was corrected. Lowered to match the CSS file so
    // the two stay in sync and neither one silently overrides the other.
    badge.style.setProperty('z-index', '2', 'important');
  }

  // Per-image size caption — reads a data-caption attribute set by
  // Liquid on the active slide's own <li class="product__media-item">
  // and shows it as a translucent banner over the bottom of the photo
  // itself (matching the reference: dukethelabel.com's PDP gallery
  // shows a caption like "Kiri is wearing our leggings in size M. She
  // is AUS 10-12.", different per photo). An earlier version of this
  // inserted the caption as a plain line of text below the image, in
  // the gap above the thumbnail strip — Alyssa's correction: it needs
  // to sit ON the photo, semi-transparent, with the image still
  // visible through it, not on the page background underneath.
  // Physically relocated into whichever slide's own image container
  // is currently active, the same append-into-the-active-container
  // approach already used for the badge and arrows above, so
  // position:absolute + bottom:0 in the CSS is relative to the photo
  // itself rather than the page.
  //
  // Originally sourced from the image's own <img alt> text, since
  // that's a native per-image field with nothing new to build. Moved
  // off alt text (per Alyssa) because alt text doubles as the
  // accessibility/SEO description, so any image whose alt text is a
  // real a11y sentence rather than a size caption would show that
  // sentence here too, and short marketing captions like "Lucia wears
  // size small" aren't good alt text either. Captions now live in a
  // single metafield, custom.gallery_captions
  // (list.single_line_text_field), matched POSITIONALLY to
  // product.media's own order rather than paired against a second
  // image-picker field — Alyssa didn't want to re-select a photo she'd
  // already uploaded just to caption it. snippets/product-media-gallery.liquid
  // resolves each media item's position and writes the matching caption
  // as data-caption="..." on that item's <li>, so this file stays a
  // plain DOM reader with no knowledge of metafields. Images with no
  // matching caption simply render data-caption="" (element hidden),
  // which is the current default for most of the gallery until
  // captions are added one by one.
  //
  // The slide carrying the model-view-toggle shows that toggle's own
  // graphic on top of the real photo underneath (see
  // relocateModelToggle() below), so even though its <li> does carry
  // its own data-caption (it's the trapped photo's own attribute),
  // showing it here would describe a picture the shopper can't
  // currently see. The toggle already has its own dedicated
  // "Size: S / Size: L" + measurements overlay for exactly this
  // purpose, so the simplest correct behaviour is no caption at all on
  // this one slide. The real photo trapped underneath still gets a
  // normal, correct caption of its own once ensureTrappedPhotoSlide()
  // clones it out into its own ordinary slide.
  function updateGalleryCaption() {
    var mediaList = document.querySelector('.product__media-list');
    if (!mediaList) return;

    var active = mediaList.querySelector('.product__media-item.is-active');
    if (!active) return;

    var text = active.querySelector('.model-view-toggle') ? '' : (active.getAttribute('data-caption') || '').trim();

    var caption = document.querySelector('.gallery-image-caption');
    if (!caption) {
      caption = document.createElement('p');
      caption.className = 'gallery-image-caption';
    }

    var target = active.querySelector('.product-media-container') || active.querySelector('.media') || active;
    if (caption.parentElement !== target) {
      target.style.setProperty('position', 'relative', 'important');
      target.appendChild(caption);
    }

    caption.textContent = text;
    caption.style.display = text ? '' : 'none';
  }

  // Dawn wires the WHOLE photo up as the zoom-modal click target by
  // default — clicking anywhere on the image opens the lightbox. The
  // actual handler lives on Dawn's own modal-opener custom element,
  // which listens for a click on the invisible full-cover button it
  // contains (.product__media-toggle), not on the visible magnify
  // glass (.product__media-icon) sitting on top of it, which is
  // aria-hidden and purely decorative by default. Alyssa wants zoom to
  // only fire when the glass itself is clicked. component-gallery-dots.css
  // disables pointer-events on .product__media-toggle and re-enables
  // them on .product__media-icon, so only the icon is clickable at
  // all — this listener is what makes that click actually still open
  // the modal, by forwarding it to the real button Dawn's own
  // modal-opener.js is listening on. Delegated on document rather than
  // bound per-icon so it keeps working automatically through Dawn's
  // variant-select re-renders (which replace gallery nodes wholesale —
  // see the enforceSingleActiveSlide comment below), with nothing to
  // re-attach.
  document.addEventListener('click', function (e) {
    var icon = e.target.closest('.product__media-icon');
    if (!icon) return;
    var opener = icon.closest('modal-opener');
    if (!opener) return;
    var toggleBtn = opener.querySelector('.product__media-toggle');
    if (toggleBtn) toggleBtn.click();
  });

  // Desktop shows the thumbnail strip as a vertical rail beside the
  // main image rather than Dawn's default horizontal strip below it
  // (component-gallery-dots.css, 750px+). That rail's height is
  // meant to match the main image's own height exactly via flexbox
  // align-items:stretch — but relying on stretch alone turned out
  // fragile in testing (it depends on the row never wrapping, on the
  // browser resolving percentage/auto heights the way we expect,
  // etc.), and Alyssa's reported symptom (thumbnails "overlapping",
  // not stopping cleanly at the bottom of the image) is exactly what
  // a silently-broken stretch looks like: instead of clipping to the
  // image's height, the rail falls back to its own content height —
  // every thumbnail stacked one after another, overflowing well past
  // the bottom of the image and down over whatever's below it.
  // Setting the thumbnail list's height explicitly in JS, matched to
  // the main image's real rendered height, removes that dependency
  // entirely — this is the one true source of "how tall should the
  // rail be", recalculated any time the image's own height could have
  // changed (resize, slide change, initial load).
  function syncThumbnailRailHeight() {
    // Height now goes on the GalleryThumbnails container itself, not
    // on .thumbnail-list directly — the container is a flex column
    // (up arrow / list / down arrow, see component-gallery-dots.css)
    // so whichever arrows are currently visible claim their own space
    // first, with the list's flex: 1 1 auto filling whatever's left.
    // Setting the height on the list directly, like before, forced it
    // to the FULL image height regardless of the arrows, pushing the
    // down arrow below the bottom of the column instead of sitting
    // under the last thumbnail — exactly the "arrow needs to be lower
    // down, under the thumbnails" gap Alyssa flagged.
    var galleryThumbs = document.querySelector('[id^="GalleryThumbnails-"]');
    if (!galleryThumbs) return;

    var isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (!isDesktop) {
      // Mobile keeps Dawn's native horizontal strip, unsized by us —
      // clear any leftover inline height from a previous desktop-width
      // session so it doesn't linger after a resize down.
      galleryThumbs.style.height = '';
      return;
    }

    var viewer = document.querySelector('[id^="GalleryViewer-"]');
    if (!viewer) return;

    // Reads the actual image content (.product__media-list) rather
    // than the slider-component's own outer box (viewer itself).
    // media-gallery is a flex row with the browser default
    // align-items: stretch, so GalleryViewer's OWN box silently
    // stretches to match whichever sibling in the row is currently
    // tallest — normally harmless, since the main image used to be
    // the taller of the two (image ~796px vs. a 5-thumbnail rail
    // capped at 760px), so viewer's stretched height and the image's
    // real height were the same number anyway. Shrinking the main
    // image (38% column width, see component-gallery-dots.css) to
    // ~666px flipped which side is taller: the rail's own capped
    // content height (760px) now exceeds the image's, so stretch
    // pulls GalleryViewer's box UP to 760px even though the image
    // inside it only renders at 666px. Reading viewer's own height at
    // that point just reads back the stretched 760px and reinforces
    // it — confirmed live, this created a stable but wrong feedback
    // loop, with the rail permanently overshooting the bottom of the
    // image by the difference (~94px on a 1512px-wide viewport).
    // .product__media-list isn't itself a flex item of that row (it's
    // a plain descendant of GalleryViewer), so its height is driven
    // purely by its one active slide's real content/aspect-ratio,
    // immune to the stretch feedback loop — confirmed live this reads
    // 666px correctly regardless of what the rail is currently doing.
    var mediaList = viewer.querySelector('.product__media-list');
    var h = (mediaList || viewer).getBoundingClientRect().height;
    if (h > 0) {
      galleryThumbs.style.height = h + 'px';
    }
  }

  // Shows/hides the up/down paging arrows (built by
  // initThumbnailRailPaging() below) based on whether the rail
  // actually has anything to scroll to in that direction. Kept as its
  // own top-level function, not nested inside the init function,
  // so it can be re-run from the same self-heal timers/listeners that
  // already exist elsewhere in this file for the badge/arrows/caption,
  // any time something could have changed how much content is
  // scrolled or how tall the rail now is.
  function updateThumbnailRailArrows() {
    var galleryThumbs = document.querySelector('[id^="GalleryThumbnails-"]');
    var thumbnailList = galleryThumbs ? galleryThumbs.querySelector('.thumbnail-list') : null;
    if (!galleryThumbs || !thumbnailList) return;

    var upBtn = galleryThumbs.querySelector('.thumbnail-rail-arrow--up');
    var downBtn = galleryThumbs.querySelector('.thumbnail-rail-arrow--down');
    if (!upBtn || !downBtn) return;

    var scrollable = thumbnailList.scrollHeight > thumbnailList.clientHeight + 1;
    var atTop = thumbnailList.scrollTop <= 1;
    var atBottom = thumbnailList.scrollTop + thumbnailList.clientHeight >= thumbnailList.scrollHeight - 1;

    upBtn.style.display = (!scrollable || atTop) ? 'none' : '';
    downBtn.style.display = (!scrollable || atBottom) ? 'none' : '';
  }

  function refreshThumbnailRail() {
    syncThumbnailRailHeight();
    updateThumbnailRailArrows();
  }

  // Alyssa's reference (mondayswimwear.com) doesn't let the rail
  // free-scroll at all — extra thumbnails past the bottom of the
  // image are fully clipped (overflow: hidden, in the CSS), and a
  // small round arrow button is the only way to page through them,
  // snapping cleanly from one full set of thumbnails to the next
  // rather than landing mid-thumbnail. Built here rather than in
  // Liquid because it only ever needs to exist on desktop and only
  // when there's actually more than one screen's worth of thumbnails
  // to page through — easier to construct on demand in JS than to
  // render (and hide) unconditionally in the template.
  //
  // Snaps to each thumbnail's own offsetTop/offsetHeight rather than
  // scrolling by a flat pixel amount (e.g. clientHeight) — a flat
  // amount only lines up with a clean thumbnail boundary by
  // coincidence, and otherwise leaves a thumbnail sliced in half
  // sitting right at the clipped edge, which is exactly the
  // "overlapping" look Alyssa flagged with the previous free-scroll
  // version of this rail.
  function initThumbnailRailPaging(galleryThumbs, thumbnailList) {
    if (!galleryThumbs || !thumbnailList) return;
    if (galleryThumbs.dataset.railPagingInit) return;
    galleryThumbs.dataset.railPagingInit = 'true';

    function buildArrow(direction) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gallery-nav-button thumbnail-rail-arrow thumbnail-rail-arrow--' + direction;
      btn.setAttribute('aria-label', direction === 'down' ? 'Show more photos' : 'Show previous photos');

      // Reuses the same icon-caret.svg Dawn already inlines for the
      // main prev/next slider buttons elsewhere on this page, rather
      // than embedding a second copy of the markup in this JS file.
      // That icon points down at its own 0deg baseline (confirmed by
      // the existing .slider-button--next/--prev rules, which rotate
      // it -90/+90deg to turn "down" into "right"/"left") — exactly
      // the orientation the down-arrow here needs already, and the
      // up-arrow just flips it 180deg.
      var existingIcon = document.querySelector('.slider-button .svg-wrapper');
      if (existingIcon) {
        var wrap = existingIcon.cloneNode(true);
        if (direction === 'up') {
          wrap.style.transform = 'rotate(180deg)';
        }
        btn.appendChild(wrap);
      } else {
        btn.textContent = direction === 'up' ? '▴' : '▾';
      }

      return btn;
    }

    var upBtn = buildArrow('up');
    var downBtn = buildArrow('down');

    upBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var items = Array.prototype.slice.call(thumbnailList.children);
      var target = thumbnailList.scrollTop - thumbnailList.clientHeight;
      var prevItem = items.slice().reverse().find(function (item) {
        return item.offsetTop <= target + 1;
      }) || items[0];
      if (prevItem) thumbnailList.scrollTo({ top: prevItem.offsetTop, behavior: 'smooth' });
    });

    downBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var items = Array.prototype.slice.call(thumbnailList.children);
      var target = thumbnailList.scrollTop + thumbnailList.clientHeight;
      var nextItem = items.find(function (item) {
        return item.offsetTop + item.offsetHeight > target - 1;
      }) || items[items.length - 1];
      if (nextItem) thumbnailList.scrollTo({ top: nextItem.offsetTop, behavior: 'smooth' });
    });

    galleryThumbs.appendChild(upBtn);
    galleryThumbs.appendChild(downBtn);

    thumbnailList.addEventListener('scroll', updateThumbnailRailArrows);
  }

  // This script loads with `defer`, so by the time it runs the DOM is
  // already fully parsed — the gallery markup already exists, it just
  // hasn't been styled by us yet. Waiting for DOMContentLoaded before
  // running relocateBadge()/relocateModelToggle() the first time added
  // a visible gap where Dawn's own native image was on screen alone,
  // then the badge/toggle popped in a beat later. Running them
  // immediately here closes that gap; the event listeners below stay
  // as a safety net in case the gallery markup renders later than
  // expected (e.g. slow connections, apps injecting content).
  relocateBadge();
  updateGalleryCaption();
  refreshThumbnailRail();
  document.addEventListener('DOMContentLoaded', relocateBadge);
  document.addEventListener('DOMContentLoaded', updateGalleryCaption);
  document.addEventListener('DOMContentLoaded', refreshThumbnailRail);
  window.addEventListener('load', relocateBadge);
  window.addEventListener('load', updateGalleryCaption);
  window.addEventListener('load', refreshThumbnailRail);
  window.addEventListener('resize', refreshThumbnailRail);

  // Re-check on scroll and resize too, throttled to one check per
  // animation frame, in case the active slide changes or anything
  // else in the DOM shifts things around.
  var badgeTicking = false;
  function scheduleBadgeCheck() {
    if (badgeTicking) return;
    badgeTicking = true;
    window.requestAnimationFrame(function () {
      relocateBadge();
      badgeTicking = false;
    });
  }
  window.addEventListener('scroll', scheduleBadgeCheck);
  window.addEventListener('resize', scheduleBadgeCheck);

  // Retry a few times shortly after load in case the gallery renders
  // its media items slightly after the initial load event.
  var badgeRetries = 0;
  var badgeRetryTimer = setInterval(function () {
    relocateBadge();
    updateGalleryCaption();
    refreshThumbnailRail();
    badgeRetries++;
    if (badgeRelocated || badgeRetries > 20) clearInterval(badgeRetryTimer);
  }, 250);

  // "Model view" toggle: lets shoppers switch between different
  // models' photos + measurements, anchored to the first/left gallery
  // image. Uses the same physically-relocate approach as the badge,
  // since positioning relative to Dawn's own wrapper isn't reliable.
  //
  // Deliberately targets the FIRST slide in DOM order, never
  // "whichever slide is currently active" — the toggle only has one
  // photo per model (not one per native gallery photo), so it can't
  // follow along as the shopper swipes through the rest of the
  // gallery without masking those other photos. Instead it lives
  // permanently on the first photo only: Dawn's own show/hide makes it
  // disappear once the shopper swipes past it, and reappear if they
  // swipe back to it.
  var modelToggleRelocated = false;

  function relocateModelToggle() {
    var toggle = document.querySelector('.model-view-toggle');
    if (!toggle) return;

    var target = document.querySelector('.product__media-item .product-media-container') ||
      document.querySelector('.product-media-container') ||
      document.querySelector('.product__media-item');
    if (!target) return;

    if (toggle.parentElement !== target) {
      target.style.setProperty('position', 'relative', 'important');
      target.insertBefore(toggle, target.firstChild);
      modelToggleRelocated = true;
    }
    toggle.classList.add('is-positioned');
  }

  relocateModelToggle();
  document.addEventListener('DOMContentLoaded', relocateModelToggle);
  window.addEventListener('load', relocateModelToggle);

  var modelToggleTicking = false;
  function scheduleModelToggleCheck() {
    if (modelToggleTicking) return;
    modelToggleTicking = true;
    window.requestAnimationFrame(function () {
      relocateModelToggle();
      modelToggleTicking = false;
    });
  }
  window.addEventListener('scroll', scheduleModelToggleCheck);
  window.addEventListener('resize', scheduleModelToggleCheck);

  var modelToggleRetries = 0;
  var modelToggleRetryTimer = setInterval(function () {
    relocateModelToggle();
    modelToggleRetries++;
    if (modelToggleRelocated || modelToggleRetries > 20) clearInterval(modelToggleRetryTimer);
  }, 250);

  // Desktop hides every slide except the active one (see the
  // display:none override in component-gallery-dots.css — Dawn's own
  // gallery here is otherwise a plain scrollable grid on desktop, not
  // a single-image view; there's no native single-image mode to rely
  // on instead). A slide that's genuinely visible during the
  // browser's own initial layout/paint loads fine, because native
  // loading="lazy" picks that up correctly. But once a shopper
  // navigates (thumbnail, dot, or arrow) and a *different* item
  // becomes the new active one, forcing it from display:none to
  // display:block via our CSS class selector doesn't reliably
  // re-trigger native lazy-loading — Chrome's lazy decision seems to
  // key off real scroll/intersection events, not a class-driven style
  // recalculation. The image is left permanently unloaded (0 natural
  // width) despite being visibly laid out on the page. Forcing
  // loading to "eager" the moment a slide becomes active immediately
  // kicks off the fetch.
  function ensureActiveImageLoaded() {
    var mediaList = document.querySelector('.product__media-list');
    if (!mediaList) return;

    var active = mediaList.querySelector('.product__media-item.is-active');
    if (!active) return;

    active.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.loading = 'eager';
    });
  }

  // Move the gallery nav arrows off the thumbnail strip and onto the
  // main image itself on desktop, so they read as real gallery
  // controls instead of getting lost down near the thumbnails. Uses
  // the same physically-relocate approach as the badge and model-view
  // toggle above, because CSS alone can't do this: the buttons live
  // inside the thumbnail strip, a sibling of the main image rather
  // than a descendant of it, so position:absolute on them can only
  // ever be relative to an ancestor they share with the thumbnail
  // row too — which would centre them against the combined height of
  // the image AND the thumbnail strip below it, not just the image.
  //
  // Mobile: hidden entirely (see component-gallery-dots.css) rather
  // than relocated — swipe plus the dots row already cover navigation
  // there, and a tap-target-sized version of these arrows would
  // crowd a portrait photo. Skip the relocation work below on mobile
  // too, since there's nothing for it to usefully do once the
  // buttons are hidden.
  function relocateGalleryArrows() {
    // Once relocated onto the main image, the buttons no longer live
    // inside [id^="GalleryThumbnails-"] — a selector scoped to that
    // container only ever finds them on the very first call. Check for
    // the class this function itself adds first, so re-runs (on every
    // slide change) can still find the buttons wherever they currently
    // are; fall back to the thumbnail-scoped selector for the initial,
    // pre-relocation lookup.
    var prevBtn = document.querySelector('.gallery-nav-button.slider-button--prev') ||
      document.querySelector('[id^="GalleryThumbnails-"] .slider-button--prev');
    var nextBtn = document.querySelector('.gallery-nav-button.slider-button--next') ||
      document.querySelector('[id^="GalleryThumbnails-"] .slider-button--next');
    if (!prevBtn || !nextBtn) return;

    prevBtn.classList.add('gallery-nav-button');
    nextBtn.classList.add('gallery-nav-button');

    var isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (!isDesktop) return;

    var mediaList = document.querySelector('.product__media-list');
    if (!mediaList) return;

    // is-active is a single class living directly on the
    // .product__media-item, toggled atomically by Dawn, which makes it
    // the one reliable, race-free signal to read here.
    var items = Array.from(mediaList.children).filter(function (el) {
      return el.classList && el.classList.contains('product__media-item');
    });
    var activeIndex = items.findIndex(function (el) {
      return el.classList.contains('is-active');
    });
    if (activeIndex === -1) return;

    var activeContainer = items[activeIndex].querySelector('.product-media-container');
    if (!activeContainer) return;

    // Only one slide is ever shown now, so both arrows live on that
    // same container.
    if (prevBtn.parentElement !== activeContainer) {
      activeContainer.style.setProperty('position', 'relative', 'important');
      activeContainer.appendChild(prevBtn);
    }

    if (nextBtn.parentElement !== activeContainer) {
      activeContainer.style.setProperty('position', 'relative', 'important');
      activeContainer.appendChild(nextBtn);
    }

    // Hide each arrow once there's genuinely nothing further in that
    // direction to reveal, rather than leaving it sitting there
    // clickable with nothing to do. Setting display to '' (not
    // 'flex' directly) falls back to whatever the CSS itself defines,
    // so this doesn't fight the .gallery-nav-button base rule.
    prevBtn.style.display = activeIndex <= 0 ? 'none' : '';
    nextBtn.style.display = activeIndex >= items.length - 1 ? 'none' : '';
  }

  relocateGalleryArrows();
  document.addEventListener('DOMContentLoaded', relocateGalleryArrows);
  window.addEventListener('load', relocateGalleryArrows);
  window.addEventListener('resize', relocateGalleryArrows);

  var galleryArrowRetries = 0;
  var galleryArrowRetryTimer = setInterval(function () {
    relocateGalleryArrows();
    galleryArrowRetries++;
    if (galleryArrowRetries > 20) clearInterval(galleryArrowRetryTimer);
  }, 250);

  var relocateTicking = false;
  function scheduleRelocateCheck() {
    if (relocateTicking) return;
    relocateTicking = true;
    window.requestAnimationFrame(function () {
      ensureActiveImageLoaded();
      relocateGalleryArrows();
      updateGalleryCaption();
      refreshThumbnailRail();
      relocateTicking = false;
    });
  }

  // Dawn's own native thumbnail-click handling keeps its own internal
  // record of "the previously active slide" and only ever clears
  // is-active from that one before adding it to the new target. Our
  // clone's is-active state is driven entirely by our own code (it has
  // no real media id for Dawn to track), so Dawn never learns the clone
  // became active — meaning the reverse handoff, a shopper clicking a
  // REAL thumbnail while the clone is showing, only adds is-active to
  // the new real slide without ever clearing it from the clone.
  // Confirmed live: after navigating into the trapped-photo clone and
  // then clicking further into the gallery, TWO slides ended up with
  // is-active simultaneously, and every index-based calculation in this
  // file (activeMediaIndex, arrow visibility bounds, goToSlide targets)
  // reads whichever one happens to come first in DOM order — silently
  // wrong from that point on.
  //
  // A MutationObserver watching for this (reacting to whichever element
  // just gained the class) was tried first and confirmed unreliable:
  // once any variant has been selected, Dawn's own gallery re-sync
  // stops simply toggling is-active on the existing elements and starts
  // physically moving/replacing slide nodes instead — which never fires
  // as an attribute mutation on a still-connected element at all, so an
  // attribute-based observer misses it outright. A plain periodic check
  // is what actually holds up regardless of which mechanism Dawn used:
  // once the clone (the only slide our own code, not Dawn, ever
  // activates) is found active alongside anything else, it's always the
  // clone that's stale — our own clonedBtn handler below already clears
  // every other slide when the clone becomes active, so a conflict can
  // only mean Dawn has since moved on to a real slide without knowing
  // to clear ours.
  var clonedSlideRef = null;

  function enforceSingleActiveSlide() {
    if (!clonedSlideRef || !clonedSlideRef.isConnected) return;
    if (!clonedSlideRef.classList.contains('is-active')) return;

    var mediaList = document.querySelector('.product__media-list');
    if (!mediaList) return;

    var items = Array.from(mediaList.children).filter(function (el) {
      return el.classList && el.classList.contains('product__media-item');
    });
    var activeCount = items.filter(function (el) {
      return el.classList.contains('is-active');
    }).length;

    if (activeCount > 1) {
      clonedSlideRef.classList.remove('is-active');
    }
  }

  // Every fix above this point (badge/toggle/arrow relocation,
  // enforceSingleActiveSlide) is reactive — it runs off DOMContentLoaded,
  // scroll/resize, or the watchActiveSlideChanges() MutationObserver.
  // Confirmed live that this whole reactive chain can go quiet: browse
  // a couple of real slides forward with the arrows, then pick a
  // variant — Dawn resets the gallery back to slide 0 (the same
  // node-replacement-style update, not a class toggle, seen earlier
  // with enforceSingleActiveSlide), so watchActiveSlideChanges() never
  // fires, relocateGalleryArrows() never re-runs, and the arrows stay
  // parented inside whatever slide was active before — which is now a
  // hidden, zero-size container, so they're still "in the DOM" and not
  // display:none, just invisible and unreachable. Manually dispatching
  // a resize event snapped them straight back, proving the relocation
  // logic itself is correct and this is purely a "never got told to
  // re-run" problem. The one-off retry timers above only run for 20
  // ticks (5 seconds) after page load, which is long past by the time
  // a shopper has browsed the gallery and picked a variant. Running
  // the same self-heal checks indefinitely, rather than only for a
  // window after load, is what actually survives Dawn triggering this
  // at any point in the page's life.
  setInterval(function () {
    enforceSingleActiveSlide();
    relocateBadge();
    relocateGalleryArrows();
    ensureActiveImageLoaded();
    updateGalleryCaption();
    refreshThumbnailRail();
  }, 300);

  function watchActiveSlideChanges() {
    var mediaList = document.querySelector('.product__media-list');
    if (!mediaList) return;

    var slideObserver = new MutationObserver(function () {
      enforceSingleActiveSlide();

      // relocateBadge() runs here directly, not folded into the
      // rAF-debounced scheduleRelocateCheck below — the class change
      // that triggers this observer already means Dawn's own CSS has
      // (synchronously) swapped which slide is visible, so moving the
      // badge in the very same tick keeps it landing in the new active
      // slide the same instant that slide's photo changes. Deferring it
      // to "next animation frame" like everything else left a one-frame
      // gap where the badge was still sitting in a container that had
      // just gone hidden, which is what actually read as it
      // jumping/flashing on every click — not the choice of which
      // slide to target.
      relocateBadge();
      updateGalleryCaption();
      scheduleRelocateCheck();
    });
    // subtree: false — is-active always lives directly on the
    // .product__media-item children themselves, never on a deeper
    // descendant, so watching only mediaList's direct children is
    // enough to catch every real navigation.
    //
    // This is deliberately NOT subtree: true. relocateGalleryArrows()
    // (added to scheduleRelocateCheck above) appends the nav buttons
    // into a .product-media-container that lives *inside* this same
    // watched subtree — confirmed live via testing that combining a
    // subtree:true watch with writes back into that subtree can spiral
    // into thousands of re-triggers a second (almost certainly from
    // some unrelated descendant — a lazy-loading image, a nested
    // widget — also touching a class attribute during the same
    // transition, which subtree:true also picks up and which our own
    // writes can keep disturbing). subtree:false removes that whole
    // class of risk while still seeing every is-active change, since
    // that's the only class attribute this code actually needs to
    // react to.
    slideObserver.observe(mediaList, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: false
    });
  }

  watchActiveSlideChanges();
  document.addEventListener('DOMContentLoaded', watchActiveSlideChanges);
  window.addEventListener('load', watchActiveSlideChanges);

  // Alyssa's report: the brown size-caption banner wasn't showing on
  // mobile. Root cause: on mobile every slide sits in one plain
  // horizontal scroll strip (is-active isn't used to hide/show slides
  // there — that forced single-slide display is a desktop-only
  // override, see the @media min-width:750 block in
  // component-gallery-dots.css), so the ACTUAL visible photo is
  // whatever the browser's native scroll-snap has scrolled into view,
  // completely independent of which slide carries the is-active class.
  // is-active was only ever being moved by an explicit thumbnail
  // click (goToSlide, initGalleryDots's own MutationObserver, this
  // file's watchActiveSlideChanges) — nothing in this file reacted to
  // a plain swipe. Confirmed live: swiping the mobile gallery visibly
  // changes the photo but leaves is-active stuck on whatever slide was
  // active before the swipe, so relocateBadge()/updateGalleryCaption()
  // keep positioning the badge/caption inside a slide container that
  // has since scrolled off-screen — reading as "the banner just isn't
  // there" even though it's still rendering, just invisible off to the
  // side.
  //
  // First attempt at this trusted Dawn's own slider-component
  // (assets/global.js), which listens for the slider's native "scroll"
  // event and dispatches a "slideChanged" CustomEvent with
  // detail.currentElement set to whichever slide it thinks is now
  // current. Confirmed live that's unreliable specifically on products
  // with the model-view-toggle: slider-component computes
  // detail.currentElement by indexing into an array of slide elements
  // (this.sliderItemsToShow) that it only builds once, at its own
  // construction time — and ensureTrappedPhotoSlide() above inserts an
  // extra synthetic slide (the trapped real photo behind the toggle)
  // into the DOM slightly after that, since this whole script loads
  // with `defer`. Every slide from that inserted clone onward is then
  // off by one in slider-component's stale array versus the real DOM,
  // so detail.currentElement pointed at the WRONG slide — confirmed
  // live via getBoundingClientRect() that it was consistently one full
  // slide-width off from whatever was actually scrolled into view.
  // Products without the toggle never insert that extra slide, so this
  // bug wouldn't show there, which is presumably why it went
  // unnoticed until Alyssa tested a toggle product on mobile.
  //
  // Computing the current slide ourselves — whichever slide's own
  // offsetLeft is numerically closest to the list's current
  // scrollLeft — reads live DOM state directly and has no dependency
  // on slider-component's cached array, so it can't be desynced by
  // our own clone insertion. Still listens on the "scroll" event
  // (rather than polling) to only do this work when the list has
  // actually moved.
  function syncActiveSlideFromNativeScroll() {
    var mediaList = document.querySelector('.product__media-list');
    if (!mediaList || mediaList.dataset.gdSlideSyncBound) return;
    mediaList.dataset.gdSlideSyncBound = 'true';

    // Alyssa's follow-up report: with a 120ms settle-debounce here, the
    // banner "disappears then reappears on each image" while scrolling.
    // Root cause: the caption element is a single reused node that's
    // physically appendChild'd into whichever slide is current — the
    // debounce meant it stayed parented inside the OUTGOING slide for
    // the full swipe gesture (120ms+, longer for a slow drag), and that
    // slide has by then scrolled out of the viewport. Confirmed live via
    // getBoundingClientRect() mid-swipe: the caption's x position had
    // gone to -364 (fully off the 375px-wide screen) while still
    // genuinely "showing" (display wasn't 'none'), reading as an outright
    // disappearance rather than an off-screen technicality — then
    // reappearing once the debounce finally fired and reparented it into
    // the new active slide, which is exactly the "disappears then
    // reappears on each image" cycle described.
    //
    // Switched to an rAF throttle instead of a settle debounce — this
    // recomputes the nearest slide on (up to) every animation frame
    // while a scroll is in progress, rather than only once after the
    // list stops moving, so the reparent happens the moment the swipe
    // crosses the midpoint between two slides instead of ~120ms+ after
    // the shopper has already stopped looking at the old one. The
    // early-return once `closest` already carries is-active (unchanged
    // below) still means the expensive relocate/caption/arrow calls only
    // fire once per genuine slide change, not on every frame — this only
    // changes WHEN that one recheck-and-possibly-reparent happens, not
    // how often the real work runs.
    var ticking = false;

    function syncNow() {
      var list = document.querySelector('.product__media-list');
      if (!list) return;

      var items = Array.from(list.children).filter(function (el) {
        return el.classList && el.classList.contains('product__media-item');
      });
      if (!items.length) return;

      var scrollLeft = list.scrollLeft;
      var closest = items[0];
      var closestDist = Math.abs(items[0].offsetLeft - scrollLeft);
      items.forEach(function (el) {
        var dist = Math.abs(el.offsetLeft - scrollLeft);
        if (dist < closestDist) {
          closest = el;
          closestDist = dist;
        }
      });

      if (closest.classList.contains('is-active')) return; // nothing changed

      items.forEach(function (el) {
        el.classList.toggle('is-active', el === closest);
      });

      // Same follow-up calls every other navigation path in this file
      // already makes after is-active changes (goToSlide, the
      // MutationObservers above) — keeps the badge, model toggle,
      // caption, relocated arrows and thumbnail rail all in step with
      // whichever slide the shopper just swiped to.
      relocateBadge();
      relocateModelToggle();
      updateGalleryCaption();
      relocateGalleryArrows();
      refreshThumbnailRail();
    }

    mediaList.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        syncNow();
        ticking = false;
      });
    });
  }

  syncActiveSlideFromNativeScroll();
  document.addEventListener('DOMContentLoaded', syncActiveSlideFromNativeScroll);
  window.addEventListener('load', syncActiveSlideFromNativeScroll);

  // Switch active model on click — updates both the highlighted
  // avatar button and which image/measurements slide is shown.
  // Bound directly on each button (rather than via delegation on
  // document) because the image container underneath likely has its
  // own click handling for zoom/swipe that calls stopPropagation(),
  // which would stop a document-level listener from ever seeing the
  // click. A listener on the button itself still fires regardless.
  function initModelToggleButtons() {
    document.querySelectorAll('.model-view-toggle__button').forEach(function (button) {
      if (button.dataset.modelToggleBound) return;
      button.dataset.modelToggleBound = 'true';

      button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var toggle = button.closest('.model-view-toggle');
        if (!toggle) return;

        var index = button.getAttribute('data-model-button');

        toggle.querySelectorAll('.model-view-toggle__button').forEach(function (b) {
          b.classList.toggle('is-active', b === button);
        });

        toggle.querySelectorAll('.model-view-toggle__slide').forEach(function (slide) {
          slide.classList.toggle('is-active', slide.getAttribute('data-model-slide') === index);
        });
      });
    });
  }

  initModelToggleButtons();
  document.addEventListener('DOMContentLoaded', initModelToggleButtons);
  window.addEventListener('load', initModelToggleButtons);

  // The model-view toggle (built by relocateModelToggle() above) is
  // deliberately permanent on whichever slide is first in DOM order —
  // the shopper always lands on the model switcher first. But its own
  // slide photos are sized to fully cover that slot, so the real photo
  // which would otherwise be there is never seen at all (confirmed
  // live: a genuinely different file to any of the toggle's own
  // photos). Rather than lose that photo, clone the first slide and its
  // thumbnail into a second, ordinary slide placed right after the
  // original — the toggle stays exactly where it is, still on the
  // original slide, and the real photo becomes a normal, fully
  // navigable slide in position two. Every later real photo just shifts
  // one position further along, same as if an extra photo had been
  // added to the gallery. This only touches the DOM the browser builds
  // for this page view — Shopify's actual product media order is never
  // written to, so anything reading that directly elsewhere (e.g. a
  // collection grid's hover-to-swap image) is unaffected.
  var trappedPhotoSlideAdded = {};

  function ensureTrappedPhotoSlide(mediaGallery, thumbnailList) {
    if (!thumbnailList) return;
    if (trappedPhotoSlideAdded[mediaGallery.id]) return;
    trappedPhotoSlideAdded[mediaGallery.id] = true;

    var mediaList = mediaGallery.querySelector('.product__media-list');
    if (!mediaList) return;

    var items = Array.from(mediaList.children).filter(function (el) {
      return el.classList && el.classList.contains('product__media-item');
    });
    var firstSlide = items[0];
    var firstThumb = thumbnailList.children[0];
    if (!firstSlide || !firstThumb) return;

    // Only the very first slide ever sits under the toggle — on a
    // product without this feature there's nothing trapped underneath
    // it, so there's nothing to duplicate.
    if (!firstSlide.querySelector('.model-view-toggle')) return;

    var clonedSlide = firstSlide.cloneNode(true);
    clonedSlide.classList.remove('is-active');
    // Lets enforceSingleActiveSlide() (defined above, polling
    // independently of this function) find this specific clone later,
    // regardless of how many media galleries end up on the page.
    clonedSlideRef = clonedSlide;
    // relocateGalleryArrows() (and, on mobile, relocateBadge()) physically
    // move the arrow buttons / badge into whichever container is
    // currently "active" — and that has already run once by the time
    // this function gets its first chance to clone firstSlide, since it
    // fires immediately at script load, before init() reaches here. If
    // any of them happened to be sitting inside firstSlide's container
    // at that moment, cloneNode(true) would duplicate them into this
    // clone as a second, dead copy — no click handler, since
    // addEventListener listeners never get cloned. Confirmed live this
    // is what broke the arrows: once the real button later moves
    // elsewhere in the DOM as the shopper navigates, this dead copy
    // (which never moves) sits earlier in document order and starts
    // winning the `document.querySelector('.gallery-nav-button...')`
    // lookups those functions rely on, leaving the real button
    // orphaned and unclickable. Strip all three relocatable singletons
    // unconditionally rather than trying to guess which one(s) were
    // present at clone time.
    ['.model-view-toggle', '.gallery-nav-button', '.custom_badge_container'].forEach(function (selector) {
      clonedSlide.querySelectorAll(selector).forEach(function (el) { el.remove(); });
    });
    // Strip ids/media-id references so nothing else on the page (Dawn's
    // own variant-image linking, anchor targets, etc.) mistakes this
    // clone for the real slide it was copied from. Confirmed live:
    // leaving data-media-id on the clone's own root (querySelectorAll
    // above only ever reached descendants, never the root itself) let
    // Dawn's variant-select handling find two slides sharing one media
    // id — on the very next variant click it reshuffled the gallery,
    // hid the real original at the end of the list, and left the clone
    // sitting in its place. Stripping data-media-id here too removes
    // the clone from that system entirely.
    clonedSlide.removeAttribute('id');
    clonedSlide.removeAttribute('data-media-id');
    clonedSlide.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
    clonedSlide.querySelectorAll('[data-media-id]').forEach(function (el) { el.removeAttribute('data-media-id'); });
    firstSlide.insertAdjacentElement('afterend', clonedSlide);

    var clonedThumb = firstThumb.cloneNode(true);
    clonedThumb.removeAttribute('id');
    clonedThumb.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
    clonedThumb.querySelectorAll('[data-media-id]').forEach(function (el) { el.removeAttribute('data-media-id'); });
    clonedThumb.classList.remove('is-active');
    clonedThumb.querySelectorAll('.is-active').forEach(function (el) { el.classList.remove('is-active'); });
    // Dawn tags every thumbnail that's linked to a specific variant's
    // image with both a "thumbnail-list_item--variant" class and a
    // data-target pointing at that image's media id — then, whenever a
    // shopper picks a variant, it shows only the ONE thumbnail whose
    // data-target matches that variant's image and hides every other
    // variant-linked thumbnail. cloneNode(true) copies both straight
    // from firstThumb, so this clone silently duplicates the exact
    // same data-target as the real thumbnail it was copied from.
    // Confirmed live: the moment any variant is picked, Dawn's pass
    // finds two thumbnails claiming that same target, keeps the
    // original visible and sets this clone to display:none — which
    // then made every click on it (arrows included, since they
    // ultimately click this same button) silently do nothing, since a
    // display:none button doesn't dispatch click events at all.
    // Stripping the class and attribute removes this clone from that
    // whole mechanism, so Dawn never touches its visibility again.
    clonedThumb.classList.remove('thumbnail-list_item--variant');
    clonedThumb.removeAttribute('data-target');
    firstThumb.insertAdjacentElement('afterend', clonedThumb);

    // firstThumb and clonedThumb are now two adjacent thumbnails showing
    // the exact same photo — firstThumb because it's a plain, unmodified
    // preview of whatever's really on slide one (the trapped photo, not
    // the toggle drawn on top of it), and clonedThumb because it's a
    // direct clone of that same slide. Confirmed live: this reads as a
    // visible duplicate in the thumbnail strip, not as two meaningfully
    // different destinations, even though clicking firstThumb jumps back
    // to the toggle while clonedThumb jumps to the plain photo. Hiding
    // firstThumb removes the visual duplicate; slide one is always where
    // shoppers land first anyway, so a dedicated "jump back to it" thumbnail
    // isn't needed. It stays in the DOM (still clickable via .click(), which
    // fires on hidden elements) purely so index-based navigation — arrows,
    // dots, goToSlide(0) — keeps working exactly as before.
    firstThumb.style.display = 'none';

    // The clone is a synthetic slide with no real Shopify media id, so
    // Dawn's own native thumbnail-click handling (which activates a
    // slide by matching that id) has nothing to match it against —
    // drive is-active for it directly instead, the same way every other
    // piece of this file already treats is-active as the one source of
    // truth for which slide is currently showing.
    var clonedBtn = clonedThumb.querySelector('button');
    if (clonedBtn) {
      clonedBtn.addEventListener('click', function (e) {
        e.preventDefault();
        // Without this, the click bubbles up to Dawn's own delegated
        // thumbnail-click handling (bound on an ancestor of the
        // thumbnail list, not on each button — so cloning this button
        // alone never escapes it). Confirmed live: Dawn's handler tries
        // to match the click back to a real media id, finds none since
        // this clone's id/data-media-id were deliberately stripped, and
        // resets the active slide back to index 0 immediately after our
        // own toggle below runs — visible as a rapid is-active
        // add/remove flicker across every slide. Stopping propagation
        // here keeps Dawn's handler from ever seeing the click.
        e.stopPropagation();
        e.stopImmediatePropagation();
        Array.from(mediaList.children).forEach(function (el) {
          if (el.classList && el.classList.contains('product__media-item')) {
            el.classList.toggle('is-active', el === clonedSlide);
          }
        });
      });
    }
  }

  function initGalleryDots(mediaGallery) {
    var sectionId = mediaGallery.id.replace('MediaGallery-', '');
    var sliderButtons = mediaGallery.querySelector('.slider-buttons');
    var counterCurrent = mediaGallery.querySelector('.slider-counter--current');
    var counterTotal = mediaGallery.querySelector('.slider-counter--total');
    var thumbnailList = document.getElementById('Slider-Thumbnails-' + sectionId);

    // Must run before `total` below is computed — it adds one real
    // thumbnail to the DOM, and that count is what everything else in
    // this function (dots row, arrow bounds) is built from.
    ensureTrappedPhotoSlide(mediaGallery, thumbnailList);

    // Desktop-only vertical rail paging (up/down arrows over the
    // clipped thumbnail column) — independent of the dots row below,
    // so it's set up even on the rare product with too few images for
    // dots to matter, and skipped harmlessly (no-op arrows, nothing to
    // scroll) when there's nothing to page through.
    var galleryThumbsEl = mediaGallery.querySelector('[id^="GalleryThumbnails-"]');
    initThumbnailRailPaging(galleryThumbsEl, thumbnailList);

    if (!sliderButtons || !counterCurrent || !counterTotal) return;

    // Dawn's own "1 / N" counter text is unreliable in this layout —
    // its total (not just current, see activeMediaIndex() below) can
    // read as low as "1" even when there are 14 real slides, which
    // was silently aborting this whole function before it ever built
    // the dots row or wired the arrows. Count the actual thumbnail
    // items in the DOM instead — that list is what goToSlide() clicks
    // into anyway, so it's the real source of truth for how many
    // navigable slides exist.
    var total = thumbnailList ? thumbnailList.children.length : parseInt(counterTotal.textContent.trim(), 10);
    if (!total || total < 2) return; // nothing to paginate

    // Build the dots row
    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'gallery-dots';
    dotsWrap.setAttribute('role', 'tablist');
    dotsWrap.setAttribute('aria-label', 'Image pagination');

    var dots = [];
    for (var i = 0; i < total; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gallery-dots__dot';
      dot.setAttribute('aria-label', 'Go to image ' + (i + 1) + ' of ' + total);
      (function (index) {
        dot.addEventListener('click', function () {
          goToSlide(index);
        });
      })(i);
      dotsWrap.appendChild(dot);
      dots.push(dot);
    }

    // Insert dots row directly after the arrows/counter bar
    sliderButtons.insertAdjacentElement('afterend', dotsWrap);

    // Keep the thumbnail strip scrolled so the active thumbnail is
    // actually visible. Individual thumbnail buttons don't scroll
    // themselves into view on click (confirmed live), and Dawn's own
    // arrow behaviour scrolled by a fixed data-step regardless of how
    // far the active slide actually moved — replaced below with a
    // handler tied to the real index instead. Computed manually
    // (rather than item.scrollIntoView) because scrollIntoView's
    // block:'nearest' still scrolled the whole page vertically here
    // (confirmed live), not just the horizontal strip.
    //
    // Desktop now shows the strip as a vertical rail beside the image
    // (component-gallery-dots.css, 750px+) rather than Dawn's default
    // horizontal strip below it, so the axis being scrolled needs to
    // switch too — mobile keeps the original horizontal-strip
    // behaviour below that same breakpoint.
    function scrollThumbnailIntoView(index) {
      if (!thumbnailList) return;
      var item = thumbnailList.children[index];
      if (!item) return;

      var isDesktop = window.matchMedia('(min-width: 750px)').matches;
      if (isDesktop) {
        var itemTop = item.offsetTop;
        var itemBottom = itemTop + item.offsetHeight;
        var viewTop = thumbnailList.scrollTop;
        var viewBottom = viewTop + thumbnailList.clientHeight;
        if (itemTop < viewTop) {
          thumbnailList.scrollTo({ top: itemTop, behavior: 'smooth' });
        } else if (itemBottom > viewBottom) {
          thumbnailList.scrollTo({ top: itemBottom - thumbnailList.clientHeight, behavior: 'smooth' });
        }
        return;
      }

      var itemLeft = item.offsetLeft;
      var itemRight = itemLeft + item.offsetWidth;
      var viewLeft = thumbnailList.scrollLeft;
      var viewRight = viewLeft + thumbnailList.clientWidth;
      if (itemLeft < viewLeft) {
        thumbnailList.scrollTo({ left: itemLeft, behavior: 'smooth' });
      } else if (itemRight > viewRight) {
        thumbnailList.scrollTo({ left: itemRight - thumbnailList.clientWidth, behavior: 'smooth' });
      }
    }

    function setActive(index) {
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === index);
        d.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      scrollThumbnailIntoView(index);
    }

    function goToSlide(index) {
      if (!thumbnailList) return;
      var item = thumbnailList.children[index];
      if (!item) return;
      var btn = item.querySelector('button');
      // Confirmed live: this is the real cause of the page "jumping" when
      // clicking the relocated arrows. btn.click() below is what actually
      // drives Dawn's own slide-selection logic — but a native .click()
      // on a <button> also moves keyboard focus onto it, and Chrome's
      // default focus behaviour auto-scrolls the whole page to bring a
      // newly-focused element into view. Most thumbnails sit lower on the
      // page than the arrows a shopper is clicking from, so every
      // navigation was yanking the page down to the thumbnail strip —
      // reproduced live as a scrollY jump from 0 to ~390px on a single
      // click. Focusing the button first with preventScroll:true marks it
      // focused without moving the viewport; .click() immediately after
      // still fires normally (Dawn's handler only cares about the click
      // event, not a fresh focus transition), so this keeps the exact
      // same navigation behaviour with none of the scroll.
      if (btn) {
        if (btn.focus) btn.focus({ preventScroll: true });
        btn.click();
      }

      // Belt-and-braces alongside the MutationObserver in
      // watchActiveSlideChanges(): dot clicks and the relocated arrows
      // both navigate through this function, so also explicitly
      // re-run the arrow and badge relocation here rather than relying
      // solely on the observer noticing the resulting is-active
      // change. That observer is subtree:false for safety (see
      // watchActiveSlideChanges()) and, confirmed live, isn't 100%
      // reliable at catching every transition on its own — several
      // clicks fired in quick succession left the badge stuck in a
      // container that had gone display:none, with nothing left to
      // move it out again. A double rAF gives Dawn's own click
      // handling a couple of frames to finish updating is-active
      // first.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (typeof relocateGalleryArrows === 'function') relocateGalleryArrows();
          if (typeof relocateBadge === 'function') relocateBadge();
          if (typeof updateGalleryCaption === 'function') updateGalleryCaption();
          if (typeof refreshThumbnailRail === 'function') refreshThumbnailRail();
        });
      });
    }

    // Dawn's own "1 / N" counter text hasn't proven reliable on this
    // layout (confirmed live: after a thumbnail click moves is-active
    // to a different slide, this text can stay stuck on "1"). Read the
    // active slide's actual DOM position instead, which does update
    // correctly on every navigation.
    var mediaList = mediaGallery.querySelector('.product__media-list');

    function activeMediaIndex() {
      if (!mediaList) return 0;
      var items = Array.from(mediaList.children).filter(function (el) {
        return el.classList && el.classList.contains('product__media-item');
      });
      var index = items.findIndex(function (el) {
        return el.classList.contains('is-active');
      });
      return index === -1 ? 0 : index;
    }

    // subtree:true below means this observer fires on ANY class mutation
    // anywhere inside mediaList, not just is-active toggling on the slides
    // themselves — and this file's own self-heal loops (badge/arrow
    // relocation, caption updates) touch classes inside this same subtree
    // roughly 3x/second. Calling setActive() unconditionally on every one of
    // those incidental mutations was harmless for the dots/counter (cheap to
    // recompute), but scrollThumbnailIntoView() inside setActive() calls
    // thumbnailList.scrollTo({top:...}) even when the active index hasn't
    // actually changed — confirmed live this is exactly what was fighting
    // the new vertical rail's paging arrows: a shopper clicks "down", the
    // rail scrolls, and within a couple hundred ms one of these unrelated
    // mutations fires the observer again, which recomputes the SAME active
    // index and snaps the rail straight back to that thumbnail's position,
    // undoing the page. Tracking the last known index and only calling
    // setActive() when it's genuinely different removes that whole class of
    // fight without changing behaviour for real slide changes at all.
    var lastActiveIndex = activeMediaIndex();
    var observer = new MutationObserver(function () {
      var idx = activeMediaIndex();
      if (idx === lastActiveIndex) return;
      lastActiveIndex = idx;
      setActive(idx);
    });
    if (mediaList) {
      observer.observe(mediaList, { attributes: true, attributeFilter: ['class'], subtree: true });
    }

    // Set initial state
    setActive(activeMediaIndex());

    // The visible prev/next arrows belong to the thumbnail strip, not
    // the main image — they only scroll the thumbnail row. Make them
    // also advance the main image, using the same current-slide index
    // the dots already track.
    //
    // relocateGalleryArrows() (in this same file) runs immediately at
    // script load, before init() reaches this function — so on desktop
    // it has often already physically moved these buttons out of
    // [id^="GalleryThumbnails-..."] and into the main image container
    // by the time we get here. Scoping the lookup to that container
    // then finds nothing, silently skipping the addEventListener
    // below and leaving only Dawn's native thumbnail-scroll behaviour
    // on the buttons (confirmed live: this was exactly the "arrows
    // only move the thumbnails" symptom). Fall back to the class
    // relocateGalleryArrows() itself tags them with, so this finds the
    // buttons regardless of which one ran first.
    var thumbArrowPrev = document.querySelector('[id^="GalleryThumbnails-' + sectionId + '"] .slider-button--prev') ||
      document.querySelector('.gallery-nav-button.slider-button--prev');
    var thumbArrowNext = document.querySelector('[id^="GalleryThumbnails-' + sectionId + '"] .slider-button--next') ||
      document.querySelector('.gallery-nav-button.slider-button--next');

    // Dawn's own native click behaviour is still bound to these
    // buttons underneath ours — it scrolls the thumbnail strip by a
    // fixed data-step="3" on every click, regardless of how far the
    // active slide actually moves. Confirmed live: one click jumped
    // the thumbnail strip ~3 thumbnails' width while only 1 image
    // advanced — both visibly janky, and over a few more clicks it
    // exhausts the strip's scrollable width long before the shopper
    // reaches the later images, so the last thumbnails become
    // impossible to scroll to. cloneNode + replace drops every
    // previously-attached listener (Dawn's included) while keeping the
    // button's classes/attributes/appearance intact; scrollThumbnailIntoView()
    // above (via setActive) replaces it with strip-scrolling that
    // actually matches the real current index.
    if (thumbArrowPrev) {
      var freshPrev = thumbArrowPrev.cloneNode(true);
      thumbArrowPrev.parentNode.replaceChild(freshPrev, thumbArrowPrev);
      thumbArrowPrev = freshPrev;
    }
    if (thumbArrowNext) {
      var freshNext = thumbArrowNext.cloneNode(true);
      thumbArrowNext.parentNode.replaceChild(freshNext, thumbArrowNext);
      thumbArrowNext = freshNext;
    }

    // Dawn also disables these buttons natively based on the thumbnail
    // strip's scroll position (e.g. "already scrolled to the start" ==
    // prev disabled) — at page load the strip's scroll is 0, so prev
    // starts out disabled, and cloneNode above copies that frozen
    // disabled="disabled" attribute onto our fresh button along with
    // it (confirmed live: this is exactly why prev silently did
    // nothing while next worked fine — a disabled button never
    // dispatches click events at all, so our own listener below never
    // even ran). Nothing goes on to correct it afterwards, since
    // Dawn's own scroll-tracking has no way to find the button once
    // it's been relocated. Our goToSlide calls already clamp with
    // Math.max(0, ...) / Math.min(total - 1, ...), so the disabled
    // attribute isn't needed for correctness — just clear it.
    if (thumbArrowPrev) {
      thumbArrowPrev.disabled = false;
      thumbArrowPrev.removeAttribute('disabled');
    }
    if (thumbArrowNext) {
      thumbArrowNext.disabled = false;
      thumbArrowNext.removeAttribute('disabled');
    }

    function currentIndex() {
      return activeMediaIndex();
    }

    // relocateGalleryArrows() parents these buttons inside
    // .product-media-container — which, on this layout, is itself
    // nested inside Dawn's own <modal-opener>, the element that opens
    // the full-screen zoom lightbox whenever anything inside it is
    // clicked. Confirmed live: clicking a relocated arrow correctly
    // advanced the slide AND popped the zoom lightbox open over the
    // whole page, because the click still bubbled up past the button
    // into modal-opener's own listener once our handler below finished.
    // Stopping propagation here doesn't touch the slide navigation
    // itself (that already happened via goToSlide, called synchronously
    // above it) — it only stops the same click going on to open the
    // lightbox.
    //
    // Reported by Alyssa: after scrolling down far enough to see the
    // caption banner near the bottom of a tall image, clicking an arrow
    // sometimes snaps the whole page back to the top. Not reproducible
    // via synthetic clicks in testing, but it lines up with a known
    // browser default: clicking a <button> natively gives it keyboard
    // focus, and if the button is only partly within the viewport at
    // that moment — likely here, since these arrows sit roughly
    // vertically centred on a 2:3 image taller than most viewports —
    // some browsers auto-scroll to bring the newly-focused element
    // fully into view as part of that native focus, independently of
    // anything goToSlide() does. goToSlide() already guards its OWN
    // focus call (on the matching thumbnail button) with
    // preventScroll:true, but that only covers that one call, not the
    // arrow button's own native focus-on-click. preventDefault() on
    // mousedown stops a button from taking focus at all, removing this
    // whole class of browser-default scroll behaviour regardless of
    // which direction it was jumping.
    if (thumbArrowPrev) {
      thumbArrowPrev.addEventListener('mousedown', function (e) {
        e.preventDefault();
      });
      thumbArrowPrev.addEventListener('click', function (e) {
        e.stopPropagation();
        goToSlide(Math.max(0, currentIndex() - 1));
      });
    }

    if (thumbArrowNext) {
      thumbArrowNext.addEventListener('mousedown', function (e) {
        e.preventDefault();
      });
      thumbArrowNext.addEventListener('click', function (e) {
        e.stopPropagation();
        goToSlide(Math.min(total - 1, currentIndex() + 1));
      });
    }

    // A shopper clicking a thumbnail image directly (rather than a dot
    // or the relocated arrows) navigates via Dawn's own click handling
    // on that thumbnail button, bypassing goToSlide() entirely — so
    // the explicit relocateGalleryArrows() call added there never
    // runs for this path. The subtree:false observer in
    // watchActiveSlideChanges() should still catch the resulting
    // is-active change, but it isn't 100% reliable in testing on this
    // layout, and unlike that observer, a real click listener can
    // never create a feedback loop, so it's a safe extra layer here.
    if (thumbnailList) {
      thumbnailList.addEventListener('click', function () {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            if (typeof relocateGalleryArrows === 'function') relocateGalleryArrows();
            if (typeof relocateBadge === 'function') relocateBadge();
          });
        });
      });
    }
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(initGalleryDots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sold-out size/cup-size options flash as available for a fraction
  // of a second, then grey out again. Two earlier attempts at this
  // (both confirmed live, both proven insufficient by later, longer
  // testing) each addressed a real but partial piece of the picture:
  // a classList-only patch missed cases where the option inputs get
  // thrown away and replaced with fresh, unstyled nodes; a
  // replacement-only patch missed cases where the *same*, still-
  // connected inputs have their own 'disabled' class removed for a
  // genuinely long stretch by whatever script (not this file) is
  // computing availability, then re-added a beat later. A screen
  // recording from the shopper finally caught the second case
  // directly, frame by frame: the sold-out sizes render fully
  // available — solid border, black text, no grey fill — for around
  // 270ms with no click, no cursor movement, and no page-blanking
  // moment anywhere nearby, ruling out a swap for that particular
  // event. Sampling actual pixel colour on the size buttons across
  // every frame of the recording was what pinned this down; classList
  // reads in a live browser test kept missing it because the event is
  // apparently tied to something that resolves a few seconds after
  // page load rather than repeating on a short, predictable interval,
  // and every prior live test happened to start watching a little too
  // late to catch it.
  //
  // This version keeps both defences rather than choosing one:
  //   1. Newly-added option inputs (from a genuine swap) are seeded
  //      with the last known-correct grey state the instant they
  //      arrive, before anything can paint them in their raw,
  //      un-greyed default.
  //   2. Every option input's own classList.add/remove/toggle is
  //      patched so that "became unavailable" always applies
  //      instantly (always safe to trust), but "became available"
  //      only actually takes effect once it's held for CONFIRM_DELAY —
  //      comfortably longer than the ~270ms flash actually measured,
  //      short enough that a genuine change (e.g. picking a different
  //      colour that really does free up a size) is never
  //      perceptibly delayed.
  (function () {
    // Found the actual script driving this (an inline theme script,
    // not this file): it recomputes every option's 'disabled' class
    // from window.products.variants both on every option change AND
    // on its own repeating setInterval(correctAvailability, 1000) —
    // every second, unconditionally, regardless of whether anything
    // actually changed. Two rounds of live testing (900ms, then
    // 1400ms) each shortened the visible flash without eliminating
    // it — confirmed live both times, the flash just got quicker
    // rather than disappearing. That pattern only makes sense if the
    // underlying bad read isn't a single one-off tick: it's holding
    // wrong across more than one 1000ms cycle in a row, so each
    // increase was masking more of it but still running out before
    // the script's own data corrected itself. Rather than keep
    // creeping this number up by trial and error, give it a much
    // wider margin — genuinely trading a little responsiveness on
    // the rare, legitimate "just became available" case (which now
    // takes up to 3s to show, still fine for something driven by a
    // shopper picking a different colour) for actually clearing
    // whatever the real bad-read duration turns out to be.
    var CONFIRM_DELAY = 3000;
    var lastKnownDisabled = Object.create(null);
    var pendingRemovals = new WeakMap();

    function stateKey(fieldsetIndex, value) {
      return fieldsetIndex + '|' + value;
    }

    // Keeps the "last known correct" snapshot fresh off whatever's
    // currently on screen, so newly-seeded inputs always have an
    // up-to-date answer to restore — including for genuine changes
    // (a shopper picking a different colour that actually does make a
    // previously sold-out size available again). Reads classList
    // directly rather than through the patched methods below, so it
    // always reflects what's actually on screen right now, including
    // during an in-progress CONFIRM_DELAY hold.
    function recordCurrentState() {
      document.querySelectorAll('variant-selects fieldset').forEach(function (fieldset, fieldsetIndex) {
        fieldset.querySelectorAll('input').forEach(function (input) {
          lastKnownDisabled[stateKey(fieldsetIndex, input.value)] = input.classList.contains('disabled');
        });
      });
    }

    function patchInput(input, fieldsetIndex) {
      if (input.dataset.gdPatched) return;
      input.dataset.gdPatched = 'true';

      // Only relevant for brand-new nodes arriving via a swap — an
      // input that already existed keeps whatever class it already
      // had, which recordCurrentState() has been tracking all along.
      var key = stateKey(fieldsetIndex, input.value);
      if (lastKnownDisabled[key]) {
        input.classList.add('disabled');
      }

      var origAdd = input.classList.add.bind(input.classList);
      var origRemove = input.classList.remove.bind(input.classList);
      var origToggle = input.classList.toggle.bind(input.classList);

      function cancelPendingRemoval() {
        var timer = pendingRemovals.get(input);
        if (timer) {
          clearTimeout(timer);
          pendingRemovals.delete(input);
        }
      }

      function scheduleRemoval() {
        if (pendingRemovals.has(input)) return; // already waiting on one
        var timer = setTimeout(function () {
          pendingRemovals.delete(input);
          origRemove('disabled');
        }, CONFIRM_DELAY);
        pendingRemovals.set(input, timer);
      }

      input.classList.add = function () {
        var args = Array.prototype.slice.call(arguments);
        if (args.indexOf('disabled') !== -1) cancelPendingRemoval();
        return origAdd.apply(input.classList, args);
      };

      input.classList.remove = function () {
        var args = Array.prototype.slice.call(arguments);
        if (args.indexOf('disabled') !== -1) {
          var rest = args.filter(function (c) { return c !== 'disabled'; });
          if (rest.length) origRemove.apply(input.classList, rest);
          scheduleRemoval();
          return;
        }
        return origRemove.apply(input.classList, args);
      };

      input.classList.toggle = function (cls, force) {
        if (cls === 'disabled') {
          if (force === false) {
            scheduleRemoval();
            return false;
          }
          cancelPendingRemoval();
          return origToggle(cls, force);
        }
        return origToggle(cls, force);
      };
    }

    function patchAllInputs() {
      document.querySelectorAll('variant-selects fieldset').forEach(function (fieldset, fieldsetIndex) {
        fieldset.querySelectorAll('input').forEach(function (input) {
          patchInput(input, fieldsetIndex);
        });
      });
    }

    var observer = new MutationObserver(function () {
      patchAllInputs();
      recordCurrentState();
    });

    function attachObserver() {
      var variantSelects = document.querySelector('variant-selects');
      if (!variantSelects || variantSelects.dataset.gdObserved) return;
      variantSelects.dataset.gdObserved = 'true';
      observer.observe(variantSelects, { childList: true, subtree: true });
    }

    recordCurrentState();
    patchAllInputs();
    attachObserver();
    document.addEventListener('DOMContentLoaded', function () {
      recordCurrentState();
      patchAllInputs();
      attachObserver();
    });
    // Belt-and-braces poll, matching this file's existing approach
    // elsewhere: re-attaches if variant-selects itself ever gets
    // swapped wholesale, and patches any input that somehow arrived
    // without going through the MutationObserver above.
    setInterval(function () {
      recordCurrentState();
      patchAllInputs();
      attachObserver();
    }, 500);
  })();
})();